import React, { useEffect, useMemo, useState, useCallback } from "react";
import dashboardConfig from "../data/dashboardConfig.json";
import { apiGet } from "../services/api";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
} from "recharts";

// ── Design tokens ─────────────────────────────────────────────────────────────

const T = {
  indigo: "#4f46e5",
  cyan: "#0891b2",
  emerald: "#10b981",
  amber: "#f59e0b",
  rose: "#f43f5e",
  slate: "#64748b",
  navy: "#0f172a",
  surface: "#ffffff",
  bg: "#f8fafc",
  border: "#e2e8f0",
  text: "#0f172a",
  muted: "#64748b",
};

const KPI_ACCENTS = {
  default: { bg: "rgba(79,70,229,0.08)", color: T.indigo },
  amber:   { bg: "rgba(245,158,11,0.10)", color: T.amber },
  blue:    { bg: "rgba(8,145,178,0.10)",  color: T.cyan },
  rose:    { bg: "rgba(244,63,94,0.10)",  color: T.rose },
  emerald: { bg: "rgba(16,185,129,0.10)", color: T.emerald },
};

const PIE_COLORS = {
  Standard:     T.indigo,
  Regulation:   T.cyan,
  Pending:      T.amber,
  "In Progress": T.cyan,
  Completed:    T.emerald,
  Overdue:      T.rose,
};

const SECTION_COLS = {
  2: "repeat(2, minmax(0,1fr))",
  3: "repeat(3, minmax(0,1fr))",
};

// ── Indian FY helpers ─────────────────────────────────────────────────────────

const MONTHS = [
  { value: 1,  label: "January"   }, { value: 2,  label: "February"  },
  { value: 3,  label: "March"     }, { value: 4,  label: "April"     },
  { value: 5,  label: "May"       }, { value: 6,  label: "June"      },
  { value: 7,  label: "July"      }, { value: 8,  label: "August"    },
  { value: 9,  label: "September" }, { value: 10, label: "October"   },
  { value: 11, label: "November"  }, { value: 12, label: "December"  },
];

function buildFYOptions() {
  const now = new Date();
  const curYear = now.getFullYear();
  const curFY = now.getMonth() < 3 ? curYear - 1 : curYear;
  const options = [];
  for (let y = curFY - 3; y <= curFY + 1; y++) {
    options.push({ value: y, label: `${y}-${String(y + 1).slice(-2)}` });
  }
  return options;
}

const FY_OPTIONS = buildFYOptions();

function defaultFY() {
  const now = new Date();
  return now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [data, setData]       = useState({ kpis: {}, charts: {} });
  const [locations, setLocations] = useState([]);

  const [filterFY,       setFilterFY]       = useState(defaultFY());
  const [filterMonth,    setFilterMonth]    = useState("");
  const [filterLocation, setFilterLocation] = useState("");

  useEffect(() => {
    apiGet("/api/locations")
      .then((resp) => setLocations(Array.isArray(resp) ? resp : resp.content || []))
      .catch(() => {});
  }, []);

  const load = useCallback((fy, month, locId) => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (fy !== "")    params.set("financialYear", fy);
    if (month !== "") params.set("month", month);
    if (locId !== "") params.set("locationId", locId);
    const query = params.toString();
    apiGet(`/api/dashboard/counts${query ? `?${query}` : ""}`)
      .then((resp) => {
        if (!cancelled) {
          setData(resp || { kpis: {}, charts: {} });
          setError(null);
        }
      })
      .catch((e) => { if (!cancelled) setError(e?.message || String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const cancel = load(filterFY, filterMonth, filterLocation);
    return cancel;
  }, [filterFY, filterMonth, filterLocation, load]);

  const kpiRow1 = dashboardConfig.kpis.slice(0, 5);
  const kpiRow2 = dashboardConfig.kpis.slice(5);

  if (error) return <ErrorState message={error} />;

  return (
    <div
      style={{
        background: T.bg,
        height: "89.8vh",
        overflowY: "auto",
        padding: "20px 24px",
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      {/* ── Filter Bar ── */}
      <FilterBar
        filterFY={filterFY}
        filterMonth={filterMonth}
        filterLocation={filterLocation}
        locations={locations}
        onFYChange={setFilterFY}
        onMonthChange={setFilterMonth}
        onLocationChange={setFilterLocation}
        loading={loading}
      />

      {loading ? (
        <LoadingState />
      ) : (
        <>
          {/* ── KPI Section 1: Regulations & Requirements ── */}
          <SectionLabel>Regulations & Requirements</SectionLabel>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: SECTION_COLS[5] || "repeat(5,minmax(0,1fr))",
              gap: 12,
              marginBottom: 12,
            }}
          >
            {kpiRow1.map((kpi) => (
              <KpiCard
                key={kpi.id}
                label={kpi.label}
                value={data.kpis[kpi.id] ?? 0}
                accent={kpi.accent || "default"}
              />
            ))}
          </div>

          {/* ── KPI Section 2: Task Overview ── */}
          <SectionLabel>Task Overview</SectionLabel>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: SECTION_COLS[6] || "repeat(6,minmax(0,1fr))",
              gap: 12,
              marginBottom: 28,
            }}
          >
            {kpiRow2.map((kpi) => (
              <KpiCard
                key={kpi.id}
                label={kpi.label}
                value={data.kpis[kpi.id] ?? 0}
                accent={kpi.accent || "default"}
              />
            ))}
          </div>

          {/* ── Chart Groups ── */}
          {dashboardConfig.chartGroups.map((group) => (
            <div key={group.label} style={{ marginBottom: 28 }}>
              <SectionLabel>{group.label}</SectionLabel>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    SECTION_COLS[group.charts.length] ||
                    `repeat(${group.charts.length}, minmax(0,1fr))`,
                  gap: 16,
                }}
              >
                {group.charts.map((chart) => (
                  <ChartCard
                    key={chart.id}
                    chart={chart}
                    rawData={data.charts[chart.id]}
                  />
                ))}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ── Filter Bar ───────────────────────────────────────────────────────────────

function FilterBar({
  filterFY, filterMonth, filterLocation, locations,
  onFYChange, onMonthChange, onLocationChange, loading,
}) {
  const selectSx = {
    fontSize: 12,
    padding: "3px 6px",
    border: `1px solid ${T.border}`,
    borderRadius: 8,
    background: T.surface,
    color: T.navy,
    outline: "none",
    cursor: "pointer",
    minWidth: 120,
    height: 30,
  };
  const labelSx = {
    fontSize: 11,
    color: T.muted,
    fontWeight: 600,
    marginRight: 6,
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 20,
        marginBottom: 20,
        padding: "10px 16px",
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: 12,
        flexWrap: "wrap",
      }}
    >
      <span style={{ fontSize: 12, fontWeight: 700, color: T.navy, marginRight: 4 }}>
        Filters
      </span>

      {/* Financial Year */}
      <div style={{ display: "flex", alignItems: "center" }}>
        <span style={labelSx}>Financial Year</span>
        <select
          style={selectSx}
          value={filterFY}
          onChange={(e) => onFYChange(e.target.value === "" ? "" : Number(e.target.value))}
          disabled={loading}
        >
          <option value="">All Years</option>
          {FY_OPTIONS.map((fy) => (
            <option key={fy.value} value={fy.value}>{fy.label}</option>
          ))}
        </select>
      </div>

      {/* Month */}
      <div style={{ display: "flex", alignItems: "center" }}>
        <span style={labelSx}>Month</span>
        <select
          style={selectSx}
          value={filterMonth}
          onChange={(e) => onMonthChange(e.target.value)}
          disabled={loading}
        >
          <option value="">All Months</option>
          {MONTHS.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </div>

      {/* Location */}
      <div style={{ display: "flex", alignItems: "center" }}>
        <span style={labelSx}>Location</span>
        <select
          style={{ ...selectSx, minWidth: 160 }}
          value={filterLocation}
          onChange={(e) => onLocationChange(e.target.value)}
          disabled={loading}
        >
          <option value="">All Locations</option>
          {locations.map((loc) => (
            <option key={loc.id} value={loc.id}>{loc.name}</option>
          ))}
        </select>
      </div>

      {/* Reset */}
      {(filterFY !== "" || filterMonth !== "" || filterLocation !== "") && (
        <button
          style={{
            fontSize: 11,
            padding: "3px 10px",
            border: `1px solid ${T.border}`,
            borderRadius: 8,
            background: "transparent",
            color: T.muted,
            cursor: "pointer",
            height: 30,
          }}
          onClick={() => {
            onFYChange(defaultFY());
            onMonthChange("");
            onLocationChange("");
          }}
          disabled={loading}
        >
          Reset
        </button>
      )}

      {loading && (
        <span style={{ fontSize: 11, color: T.muted, marginLeft: "auto" }}>
          Loading…
        </span>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: T.muted,
        marginBottom: 10,
        paddingLeft: 2,
      }}
    >
      {children}
    </div>
  );
}

function KpiCard({ label, value, accent = "default" }) {
  const { bg, color } = KPI_ACCENTS[accent] || KPI_ACCENTS.default;
  return (
    <div
      style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: 14,
        padding: "14px 16px",
        borderTop: `3px solid ${color}`,
        position: "relative",
        overflow: "hidden",
        textAlign: "center",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: bg,
          pointerEvents: "none",
        }}
      />
      <div style={{ position: "relative" }}>
        <div
          style={{
            fontSize: 11,
            color: T.muted,
            fontWeight: 500,
            marginBottom: 6,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 28,
            fontWeight: 700,
            color: T.navy,
            lineHeight: 1,
          }}
        >
          {value.toLocaleString()}
        </div>
      </div>
    </div>
  );
}

function ChartCard({ chart, rawData }) {
  const chartData = useMemo(() => {
    if (!rawData) return [];
    return Object.entries(rawData).map(([name, value]) => ({ name, value }));
  }, [rawData]);

  const isEmpty = chartData.length === 0;

  return (
    <div
      style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: 16,
        padding: "16px 18px",
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: T.navy,
          marginBottom: 14,
        }}
      >
        {chart.title}
      </div>
      {isEmpty ? (
        <EmptyChart />
      ) : chart.chartType === "bar" ? (
        <BarChartView data={chartData} color={chart.color || T.indigo} />
      ) : (
        <PieChartView data={chartData} chartId={chart.id} />
      )}
    </div>
  );
}

function BarChartView({ data, color }) {
  return (
    <div style={{ width: "100%", height: 220 }}>
      <ResponsiveContainer>
        <BarChart data={data} barCategoryGap="35%">
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: T.muted }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: T.muted }}
            axisLine={false}
            tickLine={false}
            width={30}
          />
          <Tooltip
            contentStyle={{
              borderRadius: 10,
              border: `1px solid ${T.border}`,
              fontSize: 12,
            }}
            cursor={{ fill: "rgba(79,70,229,0.05)" }}
          />
          <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function PieChartView({ data, chartId }) {
  return (
    <div style={{ width: "100%", height: 220 }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            outerRadius={75}
            innerRadius={35}
            paddingAngle={2}
          >
            {data.map((entry) => (
              <Cell key={entry.name} fill={PIE_COLORS[entry.name] || T.slate} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              borderRadius: 10,
              border: `1px solid ${T.border}`,
              fontSize: 12,
            }}
          />
          <Legend
            iconType="circle"
            iconSize={8}
            wrapperStyle={{ fontSize: 11, color: T.muted }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function EmptyChart() {
  return (
    <div
      style={{
        height: 220,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: T.muted,
        fontSize: 12,
      }}
    >
      No data available
    </div>
  );
}

function LoadingState() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "60vh",
        color: T.muted,
        fontSize: 14,
        fontFamily: "Inter, sans-serif",
      }}
    >
      Loading dashboard…
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div
      style={{
        padding: 24,
        color: T.rose,
        fontSize: 14,
        fontFamily: "Inter, sans-serif",
      }}
    >
      Failed to load dashboard: {message}
    </div>
  );
}
